import { useState } from "react";
import {
  useGetAttendanceRoster,
  useCreateAttendanceSession,
  getGetAttendanceRosterQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/auth-context";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserCheck, Loader2 } from "lucide-react";
import { fmtTime } from "@/components/attendance/attendance-list";

function todayYmd(): string {
  return new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
}

// Daily attendance register: every active member, present or absent, for one day.
// Reception can mark absentees present (skipped biometric / tailgated on another's punch).
export function DailyRoster() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [date, setDate] = useState(todayYmd);
  const { data: roster, isLoading } = useGetAttendanceRoster({ date });
  const rows = roster ?? [];

  // Deactivated (exited-this-month) members are listed for reference but are
  // excluded from the Present/Absent/Total counts — they're no longer active.
  const activeRows = rows.filter((r) => !r.deactivated);
  const present = activeRows.filter((r) => r.present).length;
  const total = activeRows.length;
  const absent = total - present;

  const create = useCreateAttendanceSession();
  const [markingId, setMarkingId] = useState<number | null>(null);

  const markPresent = async (contactId: number) => {
    setMarkingId(contactId);
    try {
      // Default entry time: now if today, else 10:00 IST on the chosen day (editable later).
      const entryAt =
        date === todayYmd()
          ? new Date().toISOString()
          : new Date(`${date}T10:00:00+05:30`).toISOString();
      await create.mutateAsync({ data: { contactId, entryAt, note: "Marked present (no biometric)" } });
      toast({ title: "Marked present" });
      queryClient.invalidateQueries({ queryKey: getGetAttendanceRosterQueryKey({ date }) });
    } catch {
      toast({ title: "Could not mark present", variant: "destructive" });
    } finally {
      setMarkingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Day</p>
          <Input type="date" value={date} max={todayYmd()} onChange={(e) => setDate(e.target.value)} className="h-8 w-[160px]" />
        </div>
        <div className="flex gap-3">
          <Card><CardContent className="px-4 py-2"><p className="text-xs text-muted-foreground">Present</p><p className="text-xl font-semibold tabular-nums text-green-600">{present}</p></CardContent></Card>
          <Card><CardContent className="px-4 py-2"><p className="text-xs text-muted-foreground">Absent</p><p className="text-xl font-semibold tabular-nums text-amber-600">{absent}</p></CardContent></Card>
          <Card><CardContent className="px-4 py-2"><p className="text-xs text-muted-foreground">Total members</p><p className="text-xl font-semibold tabular-nums">{total}</p></CardContent></Card>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4">
          <p className="mb-3 text-xs text-muted-foreground">
            Every active member for the day. Anyone who skipped the biometric (or came in on someone
            else's punch) shows <span className="font-medium">Absent</span> — mark them present manually.
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Enrolled</TableHead>
                <TableHead>Check-in</TableHead>
                <TableHead>Status</TableHead>
                {isAdmin ? <TableHead className="w-[140px] text-right">Action</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={isAdmin ? 6 : 5} className="py-6 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={isAdmin ? 6 : 5} className="py-6 text-center text-sm text-muted-foreground">No active members.</TableCell></TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow
                    key={r.contactId}
                    className={
                      r.deactivated ? "opacity-60" : r.present ? undefined : "bg-amber-50/40"
                    }
                  >
                    <TableCell className="font-medium">{r.fullName}</TableCell>
                    <TableCell className="text-muted-foreground">{r.accountName ?? "—"}</TableCell>
                    <TableCell>
                      {r.enrolled ? (
                        <span className="text-xs text-muted-foreground">ID {r.biometricId}</span>
                      ) : (
                        <Badge variant="outline" className="text-amber-600">Not enrolled</Badge>
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {!r.deactivated && r.present ? fmtTime(r.entryAt) : "—"}
                    </TableCell>
                    <TableCell>
                      {r.deactivated ? (
                        <Badge variant="outline" className="text-muted-foreground">Deactivated</Badge>
                      ) : r.present ? (
                        <Badge>Present</Badge>
                      ) : (
                        <Badge variant="secondary">Absent</Badge>
                      )}
                    </TableCell>
                    {isAdmin ? (
                      <TableCell className="text-right">
                        {!r.deactivated && !r.present ? (
                          <Button variant="outline" size="sm" className="h-7" disabled={markingId === r.contactId} onClick={() => markPresent(r.contactId)}>
                            {markingId === r.contactId ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <UserCheck className="mr-1 h-3.5 w-3.5" />}
                            Mark present
                          </Button>
                        ) : null}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
